import { Badge, Group, Paper, Stack, Text } from '@mantine/core';

type Step = {
  title: string;
  detail: string;
  state: 'complete' | 'current' | 'blocked' | 'upcoming';
};

const color = {
  complete: 'green',
  current: 'gray',
  blocked: 'orange',
  upcoming: 'gray',
} as const;

const label = {
  complete: 'Complete',
  current: 'In progress',
  blocked: 'Needs attention',
  upcoming: 'Upcoming',
} as const;

/**
 * Gives the advanced flow a visible, plain-language lifecycle without
 * duplicating server readiness or release state in the client.
 */
export default function ReleaseWorkflowProgress({
  status,
  isReady,
  hasBlockers,
}: {
  status?: 'draft' | 'frozen' | 'promoted' | 'rolled_back' | 'superseded';
  isReady: boolean;
  hasBlockers: boolean;
}) {
  const hasSavedCandidate = Boolean(status);
  const frozen = status === 'frozen';
  const publishable = frozen || status === 'promoted';
  const completed = status === 'rolled_back' || status === 'superseded';
  const steps: Step[] = [
    {
      title: 'Build candidate',
      detail: 'Choose exact automations, implementations, and execution triggers.',
      state: hasSavedCandidate ? 'complete' : 'current',
    },
    {
      title: 'Review readiness',
      detail: 'Resolve the saved candidate’s technical requirements and supporting evidence.',
      state: !hasSavedCandidate
        ? 'upcoming'
        : hasBlockers
          ? 'blocked'
          : isReady
            ? 'complete'
            : 'current',
    },
    {
      title: 'Freeze candidate',
      detail: 'Lock a technically ready candidate before publication review.',
      state:
        !hasSavedCandidate || !isReady
          ? 'upcoming'
          : publishable || completed
            ? 'complete'
            : 'current',
    },
    {
      title: 'Review and publish',
      detail: 'A human explicitly selects entrypoints, reviews the plan, and confirms it.',
      state: completed ? 'complete' : publishable ? 'current' : 'upcoming',
    },
  ];

  return (
    <Paper withBorder p="md" aria-label="Advanced release progress">
      <Stack gap="sm">
        <Text fw={600}>Release progress</Text>
        {steps.map((step, index) => (
          <Group key={step.title} align="flex-start" wrap="nowrap">
            <Badge color={color[step.state]} circle mt={2}>
              {index + 1}
            </Badge>
            <Stack gap={1} style={{ flex: 1 }}>
              <Group justify="space-between" gap="xs">
                <Text fw={500}>{step.title}</Text>
                <Badge size="sm" variant="light" color={color[step.state]}>
                  {label[step.state]}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {step.detail}
              </Text>
            </Stack>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}
